// Year > Month > Day grouping for a date column's filter, the way Excel's own date filter
// groups a real date column. Pure: HeaderFilter renders it, tests/date_tree_port.py ports it.
//
// Built from the column's DISTINCT RAW values, and a day node selects the raw values under
// it, so the filter predicate (columnFilters.ts) is untouched. A value is placed by the
// ISO date it starts with, the same rule fmtCell uses to print it as dd/mm/yyyy. Anything
// else (a source column's raw text, an aging bracket) goes to `other`, never dropped:
// every option lands in exactly one leaf.

export const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

export interface DayNode { key: string; label: string; values: string[] }
export interface MonthNode { key: string; label: string; days: DayNode[]; values: string[] }
export interface YearNode { key: string; label: string; months: MonthNode[]; values: string[] }
export interface DateTree { years: YearNode[]; other: string[] }

// Most recent first: a reconciliation reviewer's question is almost always about the latest
// months. Days within a month read forwards, like a calendar.
export function buildDateTree(options: string[]): DateTree {
  const byDay = new Map<string, string[]>();
  const other: string[] = [];
  for (const o of options) {
    const m = ISO_DATE.exec(o.trim());
    const month = m ? Number(m[2]) : 0;
    if (!m || month < 1 || month > 12) { other.push(o); continue; }
    const k = `${m[1]}-${m[2]}-${m[3]}`;
    const list = byDay.get(k);
    if (list) list.push(o); else byDay.set(k, [o]);
  }
  const years = new Map<string, Map<string, DayNode[]>>();
  for (const k of [...byDay.keys()].sort()) {
    const [y, mo, d] = k.split("-");
    let months = years.get(y);
    if (!months) { months = new Map(); years.set(y, months); }
    let days = months.get(mo);
    if (!days) { days = []; months.set(mo, days); }
    days.push({ key: k, label: `${d}/${mo}/${y}`, values: byDay.get(k)! });
  }
  const out: YearNode[] = [];
  for (const y of [...years.keys()].sort().reverse()) {
    const months: MonthNode[] = [];
    for (const mo of [...years.get(y)!.keys()].sort().reverse()) {
      const days = years.get(y)!.get(mo)!;
      months.push({ key: `${y}-${mo}`, label: MONTH_NAMES[Number(mo) - 1], days,
                    values: days.flatMap((d) => d.values) });
    }
    out.push({ key: y, label: y, months, values: months.flatMap((m) => m.values) });
  }
  return { years: out, other };
}

// The tree narrowed to what the search box names: a day matches on its dd/mm/yyyy label
// or on its "Month Year"; a year or month survives when any day under it does.
export function filterDateTree(tree: DateTree, query: string): DateTree {
  const q = query.trim().toLowerCase();
  if (!q) return tree;
  const years: YearNode[] = [];
  for (const y of tree.years) {
    const months: MonthNode[] = [];
    for (const m of y.months) {
      const monthHit = `${m.label} ${y.label}`.toLowerCase().includes(q);
      const days = monthHit ? m.days : m.days.filter((d) => d.label.includes(q));
      if (days.length) months.push({ ...m, days, values: days.flatMap((d) => d.values) });
    }
    if (months.length) years.push({ ...y, months, values: months.flatMap((m) => m.values) });
  }
  return { years, other: tree.other.filter((o) => o.toLowerCase().includes(q)) };
}

// "all" / "some" / "none" of a node's values are currently selected.
export function nodeState(values: string[], selected: Set<string>): "all" | "some" | "none" {
  let n = 0;
  for (const v of values) if (selected.has(v)) n++;
  return n === 0 ? "none" : n === values.length ? "all" : "some";
}

// Ticking a fully-selected node clears it; anything else selects all of it — Excel's rule.
export function toggleNode(values: string[], selected: string[]): string[] {
  const sel = new Set(selected);
  if (nodeState(values, sel) === "all") {
    const drop = new Set(values);
    return selected.filter((v) => !drop.has(v));
  }
  for (const v of values) sel.add(v);
  return [...sel];
}
